import type { TargetView } from '@/features/memorization/types';

/**
 * "Fokus Hari Ini" - what this learner should work on next, decided from
 * their own targets and nothing else.
 *
 * Deterministic and explainable on purpose. Every item points at a real
 * target the reader already has, and carries the reason it was picked, so
 * the list can always answer "why is this here?" with a fact rather than
 * an opinion. Nothing is inferred about the person, no score is invented,
 * and no data leaves what `GET /memorization/targets` already returned -
 * the same discipline `preferred-method-assessment.ts` follows, and the
 * reason this is a pure module rather than logic inside a component.
 *
 * It is a suggestion, never an instruction: the learner decides what they
 * actually memorize today.
 */

export type FocusReason =
  | 'NEEDS_REVISION'
  | 'OVERDUE'
  | 'DUE_SOON'
  | 'CONTINUE'
  | 'NOT_STARTED';

export interface FocusItem {
  readonly targetId: string;
  readonly surah: number;
  readonly startAyah: number;
  readonly endAyah: number;
  readonly reason: FocusReason;
  /** Days until `targetDate`; negative when it has passed, null when unset. */
  readonly daysUntilTarget: number | null;
}

/** Lower sorts first. Mirrors the order the reasons are documented above. */
const REASON_RANK: Readonly<Record<FocusReason, number>> = {
  NEEDS_REVISION: 0,
  OVERDUE: 1,
  DUE_SOON: 2,
  CONTINUE: 3,
  NOT_STARTED: 4,
};

/** A target dated this many days out or fewer counts as due soon. */
const DUE_SOON_DAYS = 3;

/** A focus list, not a second backlog - three is what fits on one screen. */
const DEFAULT_LIMIT = 3;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Whole days between two instants, counted by calendar date in the
 * viewer's own zone. Comparing raw timestamps would call a target due
 * "tomorrow" at 23:59 and "today" a minute later; comparing dates does
 * not.
 */
function daysBetween(from: Date, to: Date): number {
  const fromDay = Date.UTC(from.getFullYear(), from.getMonth(), from.getDate());
  const toDay = Date.UTC(to.getFullYear(), to.getMonth(), to.getDate());
  return Math.round((toDay - fromDay) / MS_PER_DAY);
}

/**
 * Statuses with nothing for the learner to do right now.
 *
 * SUBMITTED is deliberately here: the setoran is in, and it is an ustadz's
 * turn to evaluate it - telling the learner to work on it again would be
 * asking for duplicate work.
 */
function isSettled(target: TargetView): boolean {
  return (
    target.status === 'COMPLETED' ||
    target.status === 'CANCELLED' ||
    target.status === 'EXPIRED' ||
    target.status === 'SUBMITTED'
  );
}

function reasonFor(target: TargetView, daysUntilTarget: number | null): FocusReason {
  if (target.status === 'NEEDS_REVISION') {
    return 'NEEDS_REVISION';
  }
  if (daysUntilTarget !== null && daysUntilTarget < 0) {
    return 'OVERDUE';
  }
  if (daysUntilTarget !== null && daysUntilTarget <= DUE_SOON_DAYS) {
    return 'DUE_SOON';
  }
  if (target.status === 'IN_PROGRESS' || target.status === 'PARTIALLY_ACCEPTED') {
    return 'CONTINUE';
  }
  return 'NOT_STARTED';
}

/**
 * Ranks the learner's own targets into at most `limit` suggestions.
 *
 * Order: the reason's rank first, then the nearest deadline, then the
 * oldest target - so a fully tied pair still resolves the same way on
 * every render rather than shuffling.
 */
export function buildDailyFocus(
  targets: readonly TargetView[],
  today: Date,
  limit: number = DEFAULT_LIMIT,
): readonly FocusItem[] {
  const items: FocusItem[] = [];

  for (const target of targets) {
    if (isSettled(target)) {
      continue;
    }

    const daysUntilTarget =
      target.targetDate === null ? null : daysBetween(today, new Date(target.targetDate));

    items.push({
      targetId: target.id,
      surah: target.surah,
      startAyah: target.startAyah,
      endAyah: target.endAyah,
      reason: reasonFor(target, daysUntilTarget),
      daysUntilTarget,
    });
  }

  const byId = new Map(targets.map((target) => [target.id, target]));

  items.sort((a, b) => {
    const byReason = REASON_RANK[a.reason] - REASON_RANK[b.reason];
    if (byReason !== 0) {
      return byReason;
    }

    // An unset deadline never outranks a real one.
    const aDays = a.daysUntilTarget ?? Number.POSITIVE_INFINITY;
    const bDays = b.daysUntilTarget ?? Number.POSITIVE_INFINITY;
    if (aDays !== bDays) {
      return aDays - bDays;
    }

    const aCreated = byId.get(a.targetId)?.createdAt ?? '';
    const bCreated = byId.get(b.targetId)?.createdAt ?? '';
    return aCreated.localeCompare(bCreated);
  });

  return items.slice(0, limit);
}
